import { ReactNode } from 'react';
import { PencilIcon } from 'lucide-react';

import { cn } from '../../../lib';
import { Button, Table, TableBody } from '../../../ui';

import { ISimpleTableRowOptions, SimpleTableRow } from './SimpleTableRow';

interface ISimpleTableWidgetProps {
  actions?: ReactNode;
  className?: string;
  classNameContainer?: string;
  description: string;
  footerContent?: ReactNode;
  hasEditOption?: boolean;
  hasFaintBorders?: boolean;
  headerContent?: ReactNode;
  id?: string;
  isEditable?: boolean;
  items: ISimpleTableRowOptions[];
  onEdit?: () => void;
  title: string;
  titleClassName?: string;
  titlePrefix?: ReactNode;
  titleSuffix?: ReactNode;
}

/**
 * -----------------------------------------------------------------------------
 * Displays a simple table with duo rows and columns that can be highly customizable
 * by the parent.
 */
export function SimpleTableWidget({
  actions,
  className,
  classNameContainer,
  description,
  footerContent,
  hasEditOption = false,
  hasFaintBorders = false,
  headerContent,
  id,
  isEditable,
  items,
  onEdit,
  title,
  titleClassName,
  titlePrefix,
  titleSuffix,
}: ISimpleTableWidgetProps) {

  const handleOnEdit = () => {
    onEdit?.();
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 py-4 md:gap-6 md:py-4',
        className
      )}
      id={id}
    >
      <div
        className={cn(
          `shadow-loader-background/50 flex flex-col space-x-4 overflow-hidden
          rounded-3xl border shadow-sm`,
          classNameContainer
        )}
      >
        <div className="flex min-w-0 flex-col">
          <div className="flex min-w-0 flex-row items-center justify-between gap-2 p-6 pe-2">
            <div className="min-w-0 flex flex-1 flex-col gap-2">
              <h5
                className={cn(
                  'text-foreground flex flex-row items-center gap-2',
                  titleClassName
                )}
              >
                {titlePrefix}
                {title}
                {titleSuffix}
              </h5>
              <p className="text-text-caption text-sm">{description}</p>
            </div>

            {hasEditOption ? (
              <Button onClick={handleOnEdit} disabled={!isEditable} size="sm" variant="outline">
                <PencilIcon className="stroke-1" />
                <span className="truncate text-xs font-normal">Edit</span>
              </Button>
            ) : null}

            <div className="min-w-0 flex justify-end">{actions}</div>
          </div>
        </div>
        {headerContent}

        <Table
          className={cn(
            'w-full table-fixed border-t',
            hasFaintBorders && 'border-border/20'
          )}
        >
          <TableBody>
            {items.map(({ id: key, label, labelSuffix, value }, index) => (
              <SimpleTableRow
                key={key}
                hasFaintBorders={hasFaintBorders}
                id={key}
                index={index}
                label={label}
                labelSuffix={labelSuffix}
                value={value}
              />
            ))}
          </TableBody>
        </Table>

        {footerContent}
      </div>
    </div>
  );
}
